/**
 * The play area: one canvas with the HUD laid over it.
 *
 * Mounted by the round page and by `/replay`, identically. That is the point —
 * the renderer's only input is an `EventFeed`, so a stored session shows the
 * same kitchen, the same dockets, the same fuses, the same score and the same
 * clock the players saw, and the phase-1 architectural claim becomes something
 * a person can watch rather than something a test asserts.
 *
 * The HUD occupies a rail down each side, and the kitchen is laid out inside
 * what is left. Those insets are read from CSS custom properties on this
 * element rather than measured from the DOM every frame: a layout read inside
 * the animation frame would force a reflow fifty times a second, which is
 * exactly the cost the React/canvas split exists to avoid.
 *
 * The stage then takes its width from `stageWidth` — the two rails plus the
 * map, exactly — so there is never unused margin inside the panel.
 */

import { useEffect, useRef, useState } from 'react'
import type { EventFeed } from '../feed/feed'
import type { Catalog } from './catalog'
import { EMPTY_CATALOG } from './catalog'
import { Hud } from './hud'
import type { RecipesControl } from './RecipePanel'
import type { Insets } from './loop'
import { startRenderer, stageWidth } from './loop'

const DEFAULT_INSETS: Insets = { top: 16, right: 216, bottom: 16, left: 240 }

function readInsets(el: HTMLElement): Insets {
  const style = getComputedStyle(el)
  const px = (name: string, fallback: number): number => {
    const value = parseFloat(style.getPropertyValue(name))
    return Number.isFinite(value) ? value : fallback
  }
  return {
    top: px('--hud-top', DEFAULT_INSETS.top),
    right: px('--hud-right', DEFAULT_INSETS.right),
    bottom: px('--hud-bottom', DEFAULT_INSETS.bottom),
    left: px('--hud-left', DEFAULT_INSETS.left),
  }
}

interface StageProps {
  feed: EventFeed
  catalog?: Catalog
  /** The seat whose chef gets the self ring. `null` on the replay page. */
  selfId?: string | null
  /** Replay hides nothing, but the round page hides the HUD before it starts. */
  showHud?: boolean
  /**
   * The recipe panel's state, handed straight to the HUD. It rides through here
   * rather than being owned here because the key that toggles it is bound with
   * the movement keys on the page, and the panel itself belongs in the HUD
   * layer — over the kitchen, inside the rails, not floating beside the stage.
   */
  recipes?: RecipesControl | null
  className?: string
}

export function Stage({
  feed,
  catalog = EMPTY_CATALOG,
  selfId = null,
  showHud = true,
  recipes = null,
  className,
}: StageProps) {
  const stageRef = useRef<HTMLDivElement>(null)
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const insetsRef = useRef<Insets>(DEFAULT_INSETS)
  const selfRef = useRef<string | null>(selfId)
  const catalogRef = useRef<Catalog>(catalog)
  selfRef.current = selfId
  catalogRef.current = catalog

  /**
   * How big the kitchen is, in cells.
   *
   * A discrete value if ever there was one — it changes once, when the first
   * keyframe arrives — so it is allowed through React, and it is what stops a
   * 7x10 kitchen floating in the middle of a 16:10 letterbox with a third of
   * the frame empty on either side. The stage takes the width the map wants,
   * and no more, so the composition is the same shape as the room.
   */
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  useEffect(() => {
    let signature = ''
    return feed.subscribe((snapshot) => {
      const state = snapshot.state
      if (!state) return
      const next = `${state.mapName}:${state.stations.length}`
      if (next === signature) return
      signature = next
      let w = 0
      let h = 0
      for (const station of state.stations) {
        w = Math.max(w, station.cell.x + 1)
        h = Math.max(h, station.cell.y + 1)
      }
      setDims({ w, h })
    })
  }, [feed])

  const [maxWidth, setMaxWidth] = useState<number | null>(null)

  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const measure = (): void => {
      const insets = showHud ? readInsets(el) : { top: 8, right: 8, bottom: 8, left: 8 }
      insetsRef.current = insets
      if (!dims || dims.h === 0) return
      const next = stageWidth(dims, el.clientHeight, insets)
      setMaxWidth((previous) => (previous === next ? previous : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [showHud, dims])

  useEffect(() => {
    if (!canvas) return
    const renderer = startRenderer(canvas, feed, {
      getSelfId: () => selfRef.current,
      getCatalog: () => catalogRef.current,
      getInsets: () => insetsRef.current,
    })
    return () => renderer.stop()
  }, [canvas, feed])

  return (
    <div
      className={className ? `stage ${className}` : 'stage'}
      ref={stageRef}
      style={maxWidth ? { maxWidth } : undefined}
    >
      {/* The kitchen is a canvas, so it carries a label rather than markup. A
          full per-tile text layer would be the honest fix for a screen-reader
          player and is not in this phase; this at least names the region. */}
      <canvas
        ref={setCanvas}
        className="kitchen"
        role="img"
        aria-label="The kitchen"
      />
      {showHud && <Hud feed={feed} catalog={catalog} selfId={selfId} recipes={recipes} />}
    </div>
  )
}
